/** @file overpaintCut: a painted guide cut back into slot images, region by region, pivots from the guide. */
import { createImage } from '@zilo/pose3d-image-io/image/createImage';
import { resampleImage } from '@zilo/pose3d-image-io/image/resampleImage';
import { flatBackgroundOf } from '@zilo/pose3d-image-io/island/flatBackgroundOf';
import { foregroundMask } from '@zilo/pose3d-image-io/island/foregroundMask';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import type { CutPart, GuideRegion, GuideSheet } from './sheetTypes';
import { texelToScreen } from './texelToScreen';

/** Guide pixels a stroke may cross a region's border by and still belong to it (nearest region wins). */
const OVERPAINT_REACH = 2;
/** A painted pixel this close (every channel) to its region's guide colour is unpainted guide, not paint. */
const GUIDE_TOLERANCE = 24;
/** The 8 neighbours, the 4 edge ones first (so an edge neighbour wins a tie). */
const STEPS: readonly (readonly [number, number])[] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, -1], [1, -1], [-1, 1]];

/** The owner of a neighbour of pixel `p` that has one (-1 when none does). */
function ownedNeighbour(owners: Int16Array, W: number, H: number, p: number): number {
  const x = p % W;
  const y = (p - x) / W;
  for (const [dx, dy] of STEPS) {
    const nx = x + dx;
    const ny = y + dy;
    const o = nx >= 0 && nx < W && ny >= 0 && ny < H ? (owners[ny * W + nx] ?? -1) : -1;
    if (o >= 0) return o;
  }
  return -1;
}

/** Owners at the guide's scale, grown `reach` px into unowned pixels (each ring takes a neighbour's owner). */
function grownOwners(guide: GuideSheet, reach: number): Int16Array {
  const { width: w, height: h } = guide.placement;
  const k = guide.scale;
  const [W, H] = [w * k, h * k];
  let owners = new Int16Array(W * H);
  for (let p = 0; p < owners.length; p++) owners[p] = guide.owners[Math.floor(Math.floor(p / W) / k) * w + Math.floor((p % W) / k)] ?? -1;
  for (let ring = 0; ring < reach; ring++) {
    const next = owners.slice();
    for (let p = 0; p < owners.length; p++) if (owners[p] === -1) next[p] = ownedNeighbour(owners, W, H, p);
    owners = next;
  }
  return owners;
}

/** What one region's cut reads: the painting at guide size, its paint mask, the grown owners, the region index. */
type CutInput = { painted: Image; paint: Uint8Array; owners: Int16Array; r: number; region: GuideRegion; k: number; linear: boolean };

/** The painted pixel at (x, y) when it is paint of the region (owned, not background, not its guide colour). */
function paintAt(c: CutInput, x: number, y: number): Uint8ClampedArray | null {
  const W = c.painted.width;
  const p = y * W + x;
  if (x < 0 || y < 0 || x >= W || y >= c.painted.height || c.owners[p] !== c.r || c.paint[p] !== 1) return null;
  const px = c.painted.data.subarray(p * 4, p * 4 + 4);
  const d = Math.max(...c.region.color.slice(0, 3).map((g, ch) => Math.abs((px[ch] ?? 0) - g)));
  return d > GUIDE_TOLERANCE ? px : null;
}

/** One texel: the paint pixel under its centre (nearest), or the alpha-weighted mean of its k x k block (linear). */
function texel(c: CutInput, cx: number, cy: number): number[] | null {
  const span = c.linear ? c.k : 1;
  const x0 = Math.floor(cx) - Math.floor(span / 2);
  const y0 = Math.floor(cy) - Math.floor(span / 2);
  const sum = [0, 0, 0, 0];
  for (let j = 0; j < span * span; j++) {
    const px = paintAt(c, x0 + (j % span), y0 + Math.floor(j / span));
    if (px === null) continue;
    const a = px[3] ?? 0;
    sum.forEach((v, ch) => { sum[ch] = v + (ch === 3 ? a : (px[ch] ?? 0) * a); });
  }
  const [r = 0, g = 0, b = 0, a = 0] = sum;
  return a === 0 ? null : [r / a, g / a, b / a, a / (span * span)];
}

/** The slot image of one region: every tile texel samples the painting at its screen point times the scale. */
function cutRegion(c: CutInput): CutPart {
  const { part } = c.region;
  const image = createImage(part.size[0], part.size[1]);
  let pixels = 0;
  for (let ty = 0; ty < part.size[1]; ty++) {
    for (let tx = 0; tx < part.size[0]; tx++) {
      const [sx, sy] = texelToScreen(part, tx, ty);
      const rgba = texel(c, sx * c.k, sy * c.k);
      if (rgba === null) continue;
      image.data.set(rgba, (ty * part.size[0] + tx) * 4);
      pixels += 1;
    }
  }
  return { drawable: part.drawable, slot: part.slot, image, pixels, clipped: false };
}

/**
 * Cuts `painted` (the guide painted over; any other size is first resampled to the guide's) into one slot image per
 * region: a painted pixel belongs to the region whose guide colour lies under it, the regions grown by
 * `OVERPAINT_REACH` px into the background so a stroke past a border still counts for the nearer region. The flat
 * background the corners agree on and pixels still in the region's guide colour are not paint. Each texel of the tile
 * samples the painting at its own point on the guide, so the guide's pivot is the slot's pivot. Regions with no paint
 * are left out.
 */
export function overpaintCut(guide: GuideSheet, painted: Image): CutPart[] {
  const same = painted.width === guide.image.width && painted.height === guide.image.height;
  const fitted = same ? painted : resampleImage(painted, guide.image.width, guide.image.height, 'linear');
  const W = guide.placement.width * guide.scale;
  const H = guide.placement.height * guide.scale;
  const crop = createImage(W, H);
  for (let y = 0; y < H; y++) crop.data.set(fitted.data.subarray(y * fitted.width * 4, (y * fitted.width + W) * 4), y * W * 4);
  const paint = foregroundMask(crop, 0, flatBackgroundOf(crop, 32), 32);
  const owners = grownOwners(guide, OVERPAINT_REACH);
  const linear = guide.placement.sampling === 'linear';
  return guide.regions
    .map((region, r) => cutRegion({ painted: crop, paint, owners, r, region, k: guide.scale, linear }))
    .filter((cut) => cut.pixels > 0);
}
